import { useEffect, useState } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import api from "../lib/api";

const PREVIEW_COUNT = 6;

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [active, setActive] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/gallery").then((r) => setImages(r.data)).catch(() => setImages([]));
  }, []);

  const visible = expanded ? images : images.slice(0, PREVIEW_COUNT);
  const hasMore = images.length > PREVIEW_COUNT;

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 sm:py-32 bg-[#FAF4F1]">
      <div className="container-wide">
        <div className="reveal max-w-3xl mb-14">
          <div className="eyebrow mb-3">A look inside</div>
          <h2 className="section-title text-4xl sm:text-5xl lg:text-6xl">From the kitchen, with care.</h2>
          <p className="mt-5 text-[#4A5568] text-lg leading-relaxed">
            Real photos from our cafe — the food we plate, the drinks we pour, and the corners
            you'll fall in love with.
          </p>
        </div>

        {images.length === 0 ? (
          <p className="text-center text-[#5979A9]/60">Gallery is being curated…</p>
        ) : (
          <>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5" data-testid="gallery-grid">
              {visible.map((img, i) => (
                <button
                  key={img.id || i}
                  onClick={() => setActive(images.indexOf(img))}
                  data-testid={`gallery-item-${i}`}
                  className="block w-full break-inside-avoid overflow-hidden rounded-2xl group relative shadow-md hover:shadow-xl transition-shadow"
                >
                  <img
                    src={img.url}
                    alt={img.alt || "Cafe photo"}
                    loading="lazy"
                    className="w-full h-auto object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-[#5979A9]/0 group-hover:bg-[#5979A9]/15 transition-colors duration-500" />
                </button>
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-10">
                <button
                  onClick={() => setExpanded(!expanded)}
                  data-testid="gallery-toggle-button"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#5979A9] text-[#5979A9] font-medium hover:bg-[#5979A9] hover:text-white transition-all"
                >
                  {expanded ? (<>Show less <ChevronUp size={16}/></>) : (<>View all {images.length} photos <ChevronDown size={16}/></>)}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {active !== null && images[active] && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="lightbox-overlay"
        >
          <button
            className="absolute top-5 right-5 text-white/90 hover:text-white p-2"
            onClick={() => setActive(null)}
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <img
            src={images[active].url}
            alt={images[active].alt || ""}
            className="max-h-[88vh] max-w-[94vw] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
